/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { defineMessage, FormattedMessage } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import { Box, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

export type FollowUpResult =
	| { type: 'assigned'; agentName: string }
	| { type: 'invoke'; agentName: string }
	| { type: 'build_and_chat'; agentName: string }
	| { type: 'assign_failed' }
	| { type: 'invoke_failed' }
	| { type: 'create_failed' }
	| { type: 'draft_publish_failed' }
	| { type: 'build_and_chat_failed' }
	| { type: 'refine_in_studio'; flowKey: 'issue_assign' | 'issue_invoke' | 'build_and_chat' };

const styles = cssMap({
	wrapper: {
		paddingTop: token('space.400'),
	},
});

const assignedMessage = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.studio-create-agent.AgentCreationFollowUpText.assigned',
	defaultMessage: "I've assigned <b>{agentName}</b> to this work item.",
	description:
		'Shown below the agent card after the user successfully builds and assigns the agent to a Jira issue.',
});

const invokeMessage = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.studio-create-agent.AgentCreationFollowUpText.invoke',
	defaultMessage: "I've started a session with <b>{agentName}</b> on this work item.",
	description:
		'Shown below the agent card after the user successfully builds the agent and starts a Rovo session on the Jira issue.',
});

const buildAndChatMessage = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.studio-create-agent.AgentCreationFollowUpText.buildAndChat',
	defaultMessage: "I've opened a new chat with <b>{agentName}</b>.",
	description:
		'Shown below the agent card after the user successfully builds and publishes an agent, then opens a new Rovo chat with that agent selected. The {agentName} placeholder is the created agent name.',
});

const assignFailedMessage = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.studio-create-agent.AgentCreationFollowUpText.assignFailed',
	defaultMessage: "⚠️ Can't assign the agent. Select the <b>Assignee</b> field to try again.",
	description:
		'Shown below the agent card when the agent was built successfully but the assign mutation failed.',
});

const invokeFailedMessage = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.studio-create-agent.AgentCreationFollowUpText.invokeFailed',
	defaultMessage: "⚠️ Can't start the agent session. Select <b>Agents</b> to try again.",
	description:
		'Shown below the agent card when the agent was built successfully but the invoke mutation failed.',
});

const createFailedMessage = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.studio-create-agent.AgentCreationFollowUpText.createFailed',
	defaultMessage: 'Failed to create agent.',
	description: 'Shown below the agent card when agent creation failed.',
});

const draftPublishFailedMessage = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.studio-create-agent.AgentCreationFollowUpText.draftPublishFailed',
	defaultMessage:
		'I created a draft agent, but something went wrong. Please go to Studio to publish the agent.',
	description:
		'Shown below the agent card when the draft agent was created but publishing it failed.',
});

const buildAndChatFailedMessage = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.studio-create-agent.AgentCreationFollowUpText.buildAndChatFailed',
	defaultMessage: "⚠️ Can't start a chat with the agent. Try again.",
	description:
		'Shown below the agent card when the agent creation flow could not open a new Rovo chat with the created agent selected. The warning symbol is shown before the message.',
});

const refineInStudioIssueAssignMessage = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.studio-create-agent.AgentCreationFollowUpText.refineInStudioIssueAssign',
	defaultMessage:
		"Looks like you're making some tweaks over in Studio. Once you've published the agent, come back to the work item and get it to start working using the <b>Assignee</b> field.{br}{br}Is there anything else I can help you with today?",
	description:
		'Shown below the agent card after the user clicks "Refine in Studio" in the issue_assign flow.',
});

const refineInStudioIssueInvokeMessage = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.studio-create-agent.AgentCreationFollowUpText.refineInStudioIssueInvoke',
	defaultMessage:
		"Looks like you're making some tweaks to your agent in Studio. Once you've published it, come back to the work item and get it to start working using the <b>Agents</b> button.{br}{br}Is there anything else I can help you with today?",
	description:
		'Shown below the agent card after the user clicks "Refine in Studio" in the issue_invoke flow.',
});

const refineInStudioBuildAndChatMessage = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.studio-create-agent.AgentCreationFollowUpText.refineInStudioBuildAndChat',
	defaultMessage:
		"Looks like you're making some tweaks over in Studio. Once you've published the agent, come back to Rovo Chat and start a chat with it.{br}{br}Is there anything else I can help you with today?",
	description:
		'Shown below the agent card after the user clicks "Refine in Studio" in the build-and-chat flow. It tells the user they can publish the draft agent in Studio and start a Rovo Chat conversation with it.',
});

const richTextValues = {
	b: (chunks: React.ReactNode) => <b>{chunks}</b>,
	br: <br />,
};

type AgentCreationFollowUpTextProps = {
	followUpResult: FollowUpResult | undefined;
};

/**
 * Renders a short contextual message below the agent creation card in chat,
 * confirming the outcome of the user's chosen action (assign, invoke, refine
 * in studio, or a partial failure). Returns `null` while no action has been
 * taken yet.
 *
 * Intentionally unstyled like a card — the text sits in open chat space with
 * generous top padding so it reads as a natural follow-up message.
 */
export const AgentCreationFollowUpText = ({
	followUpResult,
}: AgentCreationFollowUpTextProps): React.JSX.Element | null => {
	if (!followUpResult) return null;

	const getMessage = (): React.ReactNode => {
		switch (followUpResult.type) {
			case 'assigned':
				return (
					<FormattedMessage
						{...assignedMessage}
						values={{ agentName: followUpResult.agentName, ...richTextValues }}
					/>
				);
			case 'invoke':
				return (
					<FormattedMessage
						{...invokeMessage}
						values={{ agentName: followUpResult.agentName, ...richTextValues }}
					/>
				);
			case 'build_and_chat':
				return (
					<FormattedMessage
						{...buildAndChatMessage}
						values={{ agentName: followUpResult.agentName, ...richTextValues }}
					/>
				);
			case 'assign_failed':
				return <FormattedMessage {...assignFailedMessage} values={richTextValues} />;
			case 'invoke_failed':
				return <FormattedMessage {...invokeFailedMessage} values={richTextValues} />;
			case 'create_failed':
				return <FormattedMessage {...createFailedMessage} values={richTextValues} />;
			case 'draft_publish_failed':
				return <FormattedMessage {...draftPublishFailedMessage} values={richTextValues} />;
			case 'build_and_chat_failed':
				return <FormattedMessage {...buildAndChatFailedMessage} values={richTextValues} />;
			case 'refine_in_studio':
				switch (followUpResult.flowKey) {
					case 'issue_assign':
						return (
							<FormattedMessage {...refineInStudioIssueAssignMessage} values={richTextValues} />
						);
					case 'issue_invoke':
						return (
							<FormattedMessage {...refineInStudioIssueInvokeMessage} values={richTextValues} />
						);
					case 'build_and_chat':
						return (
							<FormattedMessage {...refineInStudioBuildAndChatMessage} values={richTextValues} />
						);
				}
		}
	};

	const message = getMessage();
	if (!message) return null;

	return (
		<Box xcss={styles.wrapper} testId="rovo-create-agent-follow-up-text" role="status">
			<Text>{message}</Text>
		</Box>
	);
};
